import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES,
  lotteryService,
  type BetSlip,
  type BetSlipItem,
  type LotteryRound,
} from '../../services/lotteryService'
import * as walletService from '../../services/walletService'
import { getMatchedResultNumberForPrizeType, getWinningNumbers, hasAnyResult } from '../../utils/resultDisplayUtils'
import './ModernLobbyHuayForms.css'

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function formatDrawDateTime(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

/** ดึง 3ตัวบน, 2ตัวล่าง จาก round (รองรับทั้ง government และ yeekee) */
function getResultFromRound(round: LotteryRound | null): {
  threeUp: string
  twoDown: string
} {
  if (!round) return { threeUp: '', twoDown: '' }
  const yeekee = (round as { yeekee?: { result?: { threeDigitTop?: string; twoDigitDown?: string } } }).yeekee?.result
  if (yeekee) {
    return {
      threeUp: yeekee.threeDigitTop ?? '',
      twoDown: yeekee.twoDigitDown ?? '',
    }
  }
  const wn = getWinningNumbers(round)
  return { threeUp: wn.threeUp, twoDown: wn.twoDown }
}

function getItemDiscountPercent(item: BetSlipItem): number | null {
  const lockedDiscount = Number(item.lockedDiscount)
  if (Number.isFinite(lockedDiscount)) return lockedDiscount

  const snapshotDiscount = Number((item.snapshotPrizeConfig as { discount?: number } | undefined)?.discount)
  if (Number.isFinite(snapshotDiscount)) return snapshotDiscount

  const amount = Number(item.amount)
  const effectiveAmount = Number(item.effectiveAmount)
  if (Number.isFinite(amount) && amount > 0 && Number.isFinite(effectiveAmount)) {
    return ((amount - effectiveAmount) / amount) * 100
  }

  return null
}

function formatDiscountPercent(value: number | null): string {
  if (value == null) return '-'
  const normalized = Math.max(0, value)
  const rounded = Number.isInteger(normalized) ? normalized.toFixed(0) : normalized.toFixed(2)
  return `${rounded}%`
}

function getPrizeBadge(prizeAmount: number | null): {
  className: string
  text: string
} {
  if (prizeAmount == null || !Number.isFinite(prizeAmount) || prizeAmount <= 0) {
    return {
      className: '',
      text: '-',
    }
  }

  return {
    className: 'is-won',
    text: walletService.formatCurrency(prizeAmount),
  }
}

function getCustomerCancelWindowMinutes(slip: BetSlip | null): number {
  const configured = Number(slip?.customerCancelWindowMinutes)
  return Number.isFinite(configured) && configured > 0
    ? configured
    : CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES
}

function canCustomerCancelSlip(slip: BetSlip | null, round: LotteryRound | null): boolean {
  if (!slip) return false

  const status = String(slip.status || '').toLowerCase()
  if (slip.isSettled || ['cancelled', 'refunded'].includes(status)) return false

  const createdAt = slip.createdAt ? new Date(slip.createdAt).getTime() : NaN
  if (!Number.isFinite(createdAt)) return false

  if (round && String(round.status || '').toLowerCase() !== 'open') return false

  return Date.now() - createdAt <= getCustomerCancelWindowMinutes(slip) * 60 * 1000
}

function canCustomerCancelItem(
  slip: BetSlip | null,
  item: BetSlipItem,
  round: LotteryRound | null
): boolean {
  if (!canCustomerCancelSlip(slip, round)) return false

  const itemStatus = String(item.status || '').toLowerCase()
  return !['cancelled', 'refunded', 'won', 'lost'].includes(itemStatus) && item.isWinner !== true
}

function getSlipRefundDisplay(
  slip: BetSlip,
  round: LotteryRound | null
): {
  amount: number
  title: string
  subtitle: string
} {
  const cancelWindowMinutes = getCustomerCancelWindowMinutes(slip)
  const status = String(slip.status || '').toLowerCase()
  const persistedRefundAmount = Number(slip.refundAmount)
  const fallbackRefundAmount = Number(slip.totalEffectiveAmount ?? slip.totalAmount ?? 0)
  const amount = Number.isFinite(persistedRefundAmount) && persistedRefundAmount > 0
    ? persistedRefundAmount
    : fallbackRefundAmount
  const refundedAt = String(slip.refundedAt || slip.updatedAt || slip.createdAt || '')

  if (status === 'cancelled' || status === 'refunded') {
    return {
      amount: Number.isFinite(amount) ? amount : 0,
      title: 'ยอดคืนโพย',
      subtitle: `คืนโพยเมื่อ ${refundedAt ? formatDate(refundedAt) : '-'}`,
    }
  }

  if (canCustomerCancelSlip(slip, round)) {
    return {
      amount: Number.isFinite(amount) ? amount : 0,
      title: 'ค่าคืนโพย',
      subtitle: `คืนได้ภายใน ${cancelWindowMinutes} นาทีตามยอดสุทธิ`,
    }
  }

  return {
    amount: Number.isFinite(amount) ? amount : 0,
    title: 'ยอดสุทธิคืนโพย',
    subtitle: 'ระบบคืนตามยอดสุทธิของโพยเมื่อยังอยู่ในเงื่อนไข',
  }
}

/** หน้ารายละเอียดโพย — โครงสร้างตรงกับ polehuay-detail.html */
const ModernLobbyHuaySlipDetailPage: React.FC = () => {
  const { slipId } = useParams<{ slipId: string }>()
  const [slip, setSlip] = useState<BetSlip | null>(null)
  const [round, setRound] = useState<LotteryRound | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slipId) {
      setLoading(false)
      setError('ไม่พบรหัสโพย')
      return
    }
    lotteryService
      .getSlip(slipId)
      .then(async (data) => {
        setSlip(data ?? null)
        if (!data) {
          setError('ไม่พบข้อมูลโพย')
          return
        }
        const rid = data.roundId
          ? String(typeof data.roundId === 'object' ? (data.roundId as { _id?: string })._id : data.roundId)
          : null
        if (rid) {
          const [r] = await lotteryService.getLotteryRounds({ roundId: rid, limit: 1 })
          setRound(r ?? null)
        }
      })
      .catch(() => setError('โหลดโพยไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [slipId])

  const reloadSlip = async () => {
    const data = await lotteryService.getSlip(slipId || '')
    setSlip(data ?? null)
  }

  const handleCancelSlip = async () => {
    if (!slip) return
    const cancelWindowMinutes = getCustomerCancelWindowMinutes(slip)

    if (!canCustomerCancelSlip(slip, round)) {
      await Swal.fire({
        icon: 'warning',
        title: 'ไม่สามารถคืนโพยได้',
        text: `คืนโพยได้ภายใน ${cancelWindowMinutes} นาทีหลังจากที่แทง และต้องยังไม่ปิดรับแทง`,
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการคืนโพย',
      html: `คุณต้องการคืนโพย <strong>${slip.slipId ?? slip._id ?? '-'}</strong> ใช่หรือไม่?<br/><small>ระบบจะคืนยอดสุทธิของโพยกลับเข้ากระเป๋า</small>`,
      showCancelButton: true,
      confirmButtonText: 'คืนโพย',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#f59e0b',
    })

    if (!result.isConfirmed) return

    const response = await lotteryService.cancelSlip(String(slip.slipId ?? slip._id ?? ''))
    if (!response.success) {
      await Swal.fire({
        icon: 'error',
        title: 'คืนโพยไม่สำเร็จ',
        text: response.message || 'กรุณาลองใหม่อีกครั้ง',
      })
      return
    }

    const payload = response.data as { refunded?: boolean; refundError?: string | null } | undefined
    if (payload?.refunded === false) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยกเลิกโพยแล้ว แต่คืนเครดิตไม่สำเร็จ',
        text: payload.refundError || 'ระบบจะพยายามคืนเครดิตอัตโนมัติอีกครั้ง หากยอดไม่กลับภายในไม่กี่นาที กรุณาติดต่อแอดมิน',
      })
    } else {
      await Swal.fire({
        icon: 'success',
        title: 'คืนโพยสำเร็จ',
        text: response.message || 'ระบบคืนโพยเรียบร้อยแล้ว',
      })
    }

    await reloadSlip()
  }

  const handleCancelItem = async (item: BetSlipItem, itemIndex: number) => {
    if (!slip) return
    const cancelWindowMinutes = getCustomerCancelWindowMinutes(slip)

    if (!canCustomerCancelItem(slip, item, round)) {
      await Swal.fire({
        icon: 'warning',
        title: 'ไม่สามารถคืนเลขได้',
        text: `คืนเลขได้ภายใน ${cancelWindowMinutes} นาทีหลังจากที่แทง และต้องยังไม่ปิดรับแทง`,
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการคืนเลข',
      html: `คุณต้องการคืนเลข <strong>${item.number}</strong> (${item.prizeTypeName || item.prizeTypeCode}) ใช่หรือไม่?<br/><small>ระบบจะคืนยอดสุทธิของรายการนี้กลับเข้ากระเป๋า</small>`,
      showCancelButton: true,
      confirmButtonText: 'คืนเลข',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#f59e0b',
    })

    if (!result.isConfirmed) return

    const response = await lotteryService.cancelBetItem(
      String(slip.slipId ?? slip._id ?? ''),
      itemIndex,
      `User cancelled number ${item.number}`
    )
    if (!response.success) {
      await Swal.fire({
        icon: 'error',
        title: 'คืนเลขไม่สำเร็จ',
        text: response.message || 'กรุณาลองใหม่อีกครั้ง',
      })
      return
    }

    await Swal.fire({
      icon: 'success',
      title: 'คืนเลขสำเร็จ',
      text: response.message || 'ระบบคืนเลขเรียบร้อยแล้ว',
    })

    await reloadSlip()
  }

  if (loading) {
    return (
      <div className="htd-slip-detail">
        <div className="lobby-huay-page-header">
          <Link to="/lobby-huay/slip" className="lobby-huay-page-header__back">
            <i className="fas fa-chevron-left" aria-hidden /> กลับ
          </Link>
          <h3 className="lobby-huay-page-header__title">รายละเอียดโพย</h3>
        </div>
        <div className="htd-slip__state" aria-busy="true">
          <i className="fas fa-spinner fa-spin" style={{ color: '#7dd3fc', fontSize: '1.4rem' }} />
          <p>กำลังโหลด...</p>
        </div>
      </div>
    )
  }

  if (error || !slip) {
    return (
      <div className="htd-slip-detail">
        <div className="lobby-huay-page-header">
          <Link to="/lobby-huay/slip" className="lobby-huay-page-header__back">
            <i className="fas fa-chevron-left" aria-hidden /> กลับ
          </Link>
          <h3 className="lobby-huay-page-header__title">รายละเอียดโพย</h3>
        </div>
        <div className="htd-slip__state htd-slip__state--error" role="alert">
          <p>{error ?? 'ไม่พบข้อมูลโพย'}</p>
          <Link to="/lobby-huay/slip" className="htd-form__btn htd-form__btn--primary" style={{ marginTop: '0.75rem' }}>
            <i className="fas fa-chevron-left" aria-hidden />
            กลับไปโพยหวย
          </Link>
        </div>
      </div>
    )
  }

  const yeekeeResult = round
    ? (round as { yeekee?: { result?: { sumTotal?: string | number }; shotCount?: number } }).yeekee?.result
    : null
  const hasRoundResult = Boolean(hasAnyResult(round) || yeekeeResult?.sumTotal != null)

  const totalPrizeAmount = Number(slip.totalWinAmount)
  const totalPrize =
    hasRoundResult && slip.isSettled && Number.isFinite(totalPrizeAmount) && totalPrizeAmount > 0
      ? totalPrizeAmount
      : null

  const { threeUp, twoDown } = getResultFromRound(round)
  const displaySlipId = slip.slipId ?? slip._id ?? '-'
  const sumTotal = yeekeeResult?.sumTotal != null ? String(yeekeeResult.sumTotal) : null
  const canCancelNow = canCustomerCancelSlip(slip, round)
  const refundDisplay = getSlipRefundDisplay(slip, round)

  return (
    <div className="htd-slip-detail">
      <div className="lobby-huay-page-header">
        <Link to="/lobby-huay/slip" className="lobby-huay-page-header__back">
          <i className="fas fa-chevron-left" aria-hidden /> กลับ
        </Link>
        <h3 className="lobby-huay-page-header__title">รายละเอียดโพย</h3>
        {canCancelNow ? (
          <div className="htd-slip-detail__header-actions">
            <button type="button" className="htd-slip__cancel-btn" onClick={() => void handleCancelSlip()}>
              คืนโพย
            </button>
          </div>
        ) : null}
      </div>

      <section className="htd-slip-detail__summary" aria-label="สรุปโพย">
        <div className="htd-slip-detail__code">
          <i className="fas fa-clipboard-list" aria-hidden />
          รหัสการแทง {displaySlipId}
        </div>

        <div className="htd-slip-detail__names">
          <strong>{slip.huayName ?? '-'}</strong>
          <span>
            {round?.betEndTime
              ? formatDrawDateTime(round.betEndTime)
              : slip.roundNumber
                ? `รอบที่ ${slip.roundNumber}`
                : '-'}
          </span>
        </div>

        <div className="htd-slip-detail__when">
          <i className="fas fa-calendar-alt" aria-hidden />
          ทำรายการเมื่อ {slip.createdAt ? formatDate(slip.createdAt) : '-'}
        </div>

        <div className="htd-slip-detail__metrics">
          <div className="htd-slip-detail__metric">
            <small>เงินเดิมพันทั้งหมด</small>
            <strong>{walletService.formatCurrency(slip.totalAmount ?? 0)}</strong>
          </div>
          <div className={`htd-slip-detail__metric${totalPrize != null ? ' is-won' : ''}`}>
            <small>ถูกรางวัล</small>
            <strong>{totalPrize != null ? walletService.formatCurrency(totalPrize) : '-'}</strong>
          </div>
          <div className="htd-slip-detail__metric htd-slip-detail__metric--wide">
            <small>{refundDisplay.title}</small>
            <strong>{walletService.formatCurrency(refundDisplay.amount)}</strong>
            <em>{refundDisplay.subtitle}</em>
          </div>
        </div>
      </section>

      {(threeUp || twoDown || round) ? (
        <section aria-label="ผลรางวัลที่ออก">
          <h4 className="htd-slip-detail__section-title">ผลรางวัลที่ออก</h4>
          <div className="htd-slip-detail__results" style={{ marginTop: '0.55rem' }}>
            <div className="htd-slip-detail__result">
              <small>3 ตัวบน</small>
              <strong>{threeUp || 'รอผล'}</strong>
            </div>
            <div className="htd-slip-detail__result">
              <small>2 ตัวล่าง</small>
              <strong>{twoDown || 'รอผล'}</strong>
            </div>
          </div>
        </section>
      ) : null}

      <section aria-label="รายการแทง">
        <h4 className="htd-slip-detail__section-title">รายการแทง</h4>
        <div className="htd-slip-detail__items" style={{ marginTop: '0.55rem' }}>
          {slip.betItems && slip.betItems.length > 0 ? (
            [...slip.betItems]
              .map((item, originalIndex) => ({ item, originalIndex }))
              .sort((a, b) => {
                const rank = (it: BetSlipItem) => {
                  if (!hasRoundResult) return 1
                  const won = (it.isWinner as boolean) ?? it.status === 'won'
                  return won ? 0 : 1
                }
                const diff = rank(a.item) - rank(b.item)
                return diff !== 0 ? diff : a.originalIndex - b.originalIndex
              })
              .map(({ item, originalIndex }) => {
              const itemStatus = String(item.status || '').toLowerCase()
              const isCancelledItem = ['cancelled', 'refunded'].includes(itemStatus)
              const isWinner = hasRoundResult
                ? ((item.isWinner as boolean) ?? itemStatus === 'won')
                : null
              const discountPercent = getItemDiscountPercent(item)
              const creditedAmount = Number(item.winAmount ?? item.potentialPayout)
              const prizeBadge = getPrizeBadge(
                isWinner && Number.isFinite(creditedAmount) ? creditedAmount : null
              )
              const statusClass = isCancelledItem
                ? 'is-cancelled'
                : isWinner === null
                  ? 'is-pending'
                  : isWinner
                    ? 'is-won'
                    : 'is-lost'
              const statusLabel = isCancelledItem
                ? 'ยกเลิกแล้ว'
                : isWinner === null
                  ? 'รอผล'
                  : isWinner
                    ? 'ถูกรางวัล'
                    : 'ไม่ถูกรางวัล'
              const canCancelItemNow = canCustomerCancelItem(slip, item, round)

              return (
                <article
                  key={`${item.number}-${item.prizeTypeCode}-${originalIndex}`}
                  className={`htd-slip-detail__item${isWinner ? ' is-won-item' : ''}${isCancelledItem ? ' is-cancelled-item' : ''}`}
                >
                  <div className="htd-slip-detail__item-top">
                    <h6>ลำดับที่ {originalIndex + 1}</h6>
                    <div className="htd-slip-detail__item-actions">
                      {canCancelItemNow ? (
                        <button
                          type="button"
                          className="htd-slip__cancel-btn"
                          onClick={() => void handleCancelItem(item, originalIndex)}
                        >
                          คืนเลข
                        </button>
                      ) : null}
                      <span className={`htd-slip-detail__status ${statusClass}`}>
                        {statusLabel}
                      </span>
                    </div>
                  </div>

                  <div className="htd-slip-detail__item-body">
                    <div className="htd-slip-detail__number">
                      <strong>{item.number}</strong>
                      <span className="htd-slip-detail__type">
                        {item.prizeTypeName ?? item.prizeTypeCode ?? '-'}
                      </span>
                    </div>

                    <div className="htd-slip-detail__item-stats">
                      <div className="htd-slip-detail__stat">
                        <small>เลขที่ออก</small>
                        <strong>
                          {hasRoundResult
                            ? getMatchedResultNumberForPrizeType(item.prizeTypeCode, round) || 'รอผล'
                            : 'รอผล'}
                        </strong>
                      </div>
                      <div className="htd-slip-detail__stat">
                        <small>จ่าย</small>
                        <strong>
                          {item.lockedPayoutRate ??
                            (item.potentialPayout != null && item.amount
                              ? Math.round((item.potentialPayout ?? 0) / item.amount)
                              : '-')}
                        </strong>
                      </div>
                      <div className="htd-slip-detail__stat">
                        <small>ลด</small>
                        <strong>{formatDiscountPercent(discountPercent)}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="htd-slip-detail__item-foot">
                    <div>
                      ราคาแทง
                      <div className="htd-slip-detail__pill">
                        {walletService.formatCurrency(item.amount ?? 0)}
                      </div>
                    </div>
                    <div>
                      ถูกรางวัล
                      <div className={`htd-slip-detail__pill ${prizeBadge.className}`}>
                        {prizeBadge.text}
                      </div>
                    </div>
                  </div>
                </article>
              )
            })
          ) : (
            <div className="htd-slip__state">
              <p>ไม่มีรายการแทง</p>
            </div>
          )}
        </div>
      </section>

      {(sumTotal || round) ? (
        <section className="htd-slip-detail__sum" aria-label="ผลรวมเลข">
          <h4 className="htd-slip-detail__section-title">ผลรวมเลข</h4>
          <p className="htd-slip-detail__sum-value">{sumTotal || 'รอผล'}</p>
          <div className="htd-slip-detail__results">
            <div className="htd-slip-detail__result">
              <small>รอบวันที่</small>
              <strong style={{ fontSize: '1rem' }}>
                {round?.betEndTime ? formatDrawDateTime(round.betEndTime) : slip.roundNumber ?? '-'}
              </strong>
            </div>
            <div className="htd-slip-detail__result">
              <small>รอบที่</small>
              <strong style={{ fontSize: '1rem' }}>{slip.roundNumber ?? '-'}</strong>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  )
}

export default ModernLobbyHuaySlipDetailPage
